import React, { useState } from 'react';
import { Button, Card, Space, Typography, Divider, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FieldConfig } from '../../types/fieldTypes';
import { DynamicField } from '../DynamicField';

const { Title } = Typography;

interface ArrayFieldProps {
  field: FieldConfig;
  value: any[];
  onChange: (value: any[]) => void;
  mode?: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const ArrayField: React.FC<ArrayFieldProps> = ({
  field,
  value = [],
  onChange,
  mode = 'edit',
  formData,
}) => {
  const { t } = useTranslation();
  // Initialize with default items if specified
  const defaultItems = field.customProps?.defaultItems || 0;
  const initialValue =
    value && value.length > 0 ? value : Array(defaultItems).fill({});

  const [arrayData, setArrayData] = useState<any[]>(initialValue);

  const handleAddItem = () => {
    const newItem = {};
    const updatedArray = [...arrayData, newItem];
    setArrayData(updatedArray);
    onChange(updatedArray);
  };

  const handleRemoveItem = (index: number) => {
    const updatedArray = arrayData.filter((_, i) => i !== index);
    setArrayData(updatedArray);
    onChange(updatedArray);
  };

  const handleItemChange = (index: number, itemData: any) => {
    const updatedArray = arrayData.map((item, i) =>
      i === index ? { ...item, ...itemData } : item
    );
    setArrayData(updatedArray);
    onChange(updatedArray);
  };

  const handleFieldChange = (
    index: number,
    fieldName: string,
    fieldValue: any
  ) => {
    const updatedArray = arrayData.map((item, i) =>
      i === index ? { ...item, [fieldName]: fieldValue } : item
    );
    setArrayData(updatedArray);
    onChange(updatedArray);
  };

  if (mode === 'view') {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        {arrayData.map((item, index) => (
          <Card key={index} size="small">
            <Title level={5}>Item {index + 1}</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {field.customProps?.itemFields?.map((itemField: any) => (
                <div key={itemField.name}>
                  <strong>{itemField.label}:</strong>{' '}
                  {item[itemField.name] || 'N/A'}
                </div>
              ))}
            </Space>
          </Card>
        ))}
      </Space>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <Button
        type="dashed"
        onClick={handleAddItem}
        icon={<PlusOutlined />}
        block
        style={{ marginBottom: 16 }}
      >
        {field.customProps?.addButtonText || t('dynamic_form.add_item')}
      </Button>

      {arrayData.map((item, index) => (
        <Card
          key={index}
          size="small"
          style={{ marginBottom: 16, width: '100%' }}
        >
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {field.customProps?.itemTitle || `Item ${index + 1}`}
              </Title>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveItem(index)}
              >
                Remove
              </Button>
            </div>

            <Divider />

            <Row gutter={[16, 16]} style={{ width: '100%' }}>
              {field.customProps?.itemFields?.map((itemField: any) => (
                <Col key={itemField.name} span={itemField.layout?.span || 24}>
                  <DynamicField
                    field={{
                      ...itemField,
                      name: `${field.name}[${index}].${itemField.name}`,
                      layout: { span: 24 }, // Force full width within the Col
                    }}
                    value={item[itemField.name] || ''}
                    onChange={(e: any) => {
                      const fieldValue = e.target ? e.target.value : e;
                      handleFieldChange(index, itemField.name, fieldValue);
                    }}
                    mode={mode}
                    formData={formData}
                  />
                </Col>
              ))}
            </Row>
          </div>
        </Card>
      ))}

      {arrayData.length === 0 && (
        <Card style={{ width: '100%' }}>
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No items added yet. Click "Add Item" to get started.
          </div>
        </Card>
      )}
    </div>
  );
};
